import type { TFunction } from 'i18next';

/** MUI Chip color for status chips */
export type BillingChipColor = 'success' | 'warning' | 'error' | 'info' | 'default';

/** GastroSoft.Cloud.Billing — AccountingDocumentState (GraphQL: UPPER_SNAKE) */
export const accountingDocumentStateColors: Record<string, BillingChipColor> = {
  DRAFT: 'default',
  SENT: 'info',
  PAID: 'success',
  OVERDUE: 'error',
  CANCELLED: 'error',
};

/** GastroSoft.Cloud.Billing — PaymentState */
export const paymentStateColors: Record<string, BillingChipColor> = {
  PENDING: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  REFUNDED: 'info',
  REFUND_PENDING: 'warning',
};

/** GastroSoft.Cloud.Billing — PaymentEventType (payment_history.event_type). */
export const paymentEventTypeColors: Record<string, BillingChipColor> = {
  CHARGED_BACK: 'error',
  COMPLETED: 'success',
  CREATED: 'default',
  EVENT_SKIPPED: 'default',
  FAILED: 'error',
  REFUNDED: 'info',
  REFUND_FAILED: 'error',
  REFUND_SUBMITTED: 'info',
  SUBMITTED: 'info',
};

/** Subscription state (API) */
export const subscriptionStateColors: Record<string, BillingChipColor> = {
  ACTIVE: 'success',
  PENDING: 'warning',
  CANCELLED: 'error',
  EXPIRED: 'error',
  SUSPENDED: 'warning',
};

/** GastroSoft.Cloud.Billing — LicenceState */
export const licenceStateColors: Record<string, BillingChipColor> = {
  ACTIVE: 'success',
  PENDING: 'warning',
  EXPIRED: 'error',
  REVOKED: 'error',
  SUSPENDED: 'warning',
};

/** GastroSoft.Cloud.Billing — InvitationState */
export const invitationStateColors: Record<string, BillingChipColor> = {
  SENT: 'info',
  ACCEPTED: 'warning',
  COMPLETED: 'success',
  REVOKED: 'default',
  FAILED: 'error',
};

export function translateAccountingDocumentState(t: TFunction, state: string): string {
  return t(`billingStates.accountingDocument.${state}`, { defaultValue: state });
}

export function translatePaymentState(t: TFunction, state: string): string {
  return t(`billingStates.payment.${state}`, { defaultValue: state });
}

export function translateSubscriptionState(t: TFunction, state: string): string {
  return t(`billingStates.subscription.${state}`, { defaultValue: state });
}

export function translateLicenceState(t: TFunction, state: string): string {
  return t(`billingStates.licence.${state}`, { defaultValue: state });
}

export function translateInvitationState(t: TFunction, state: string): string {
  return t(`billingStates.invitation.${state}`, { defaultValue: state });
}

/** GastroSoft.Cloud.Billing — AppType */
export function translateAppType(t: TFunction, appType: string): string {
  return t(`billingStates.appType.${appType}`, { defaultValue: appType });
}

/** GastroSoft.Cloud.Billing — LicenceType */
export function translateLicenceType(t: TFunction, licenceType: string): string {
  return t(`billingStates.licenceType.${licenceType}`, { defaultValue: licenceType });
}

/** GastroSoft.Cloud.Billing — PaymentDirection (IN / OUT) */
export function translatePaymentDirection(t: TFunction, direction: string): string {
  return t(`billingStates.paymentDirection.${direction}`, { defaultValue: direction });
}

/** GastroSoft.Cloud.Billing — PaymentEventType (append-only history rows). */
export function translatePaymentEventType(t: TFunction, eventType: string): string {
  return t(`billingStates.paymentEventType.${eventType}`, { defaultValue: eventType });
}
